import { resolvePrice } from '../../../shared/utils/productUtils.js'
import { sumSunLightRanges } from '../../../shared/utils/sunLightUtils.js'
import { getInsolationBucket } from '../../../shared/utils/productInsolation.js'
import { buildProductAreaDeciles } from '../../../shared/utils/productAreaDeciles.js'
import { ALL_PRODUCT_CATEGORIES } from '../../../shared/constants/productCategories.js'

// import { FILTER_IDS, UNIT_LEVELS, RANGE_STEPS, classifyExpositionSides, EXPOSITION_SIDES_ORDER } from './filterRegistry.js'
import { FILTER_IDS, UNIT_LEVELS, RANGE_STEPS, EXPOSITION_SIDES_ORDER } from '../constants/filterConstants.js'
import { classifyExpositionSides } from '../../../shared/utils/productExpositionSides.js'


// ── Private helpers ───────────────────────────────────────────────────────────
// Each computes the "full range / all options" value for one filter key.
// Only called on already-filtered displayable products.

function getRoomsRange(products) {
  const values = products.map(p => p.rooms).filter(r => r != null && !isNaN(r))
  if (values.length === 0) return [1, 1]
  return [Math.min(...values), Math.max(...values)]
}

function getAreaRange(products) {
  const step = RANGE_STEPS[FILTER_IDS.AREA]
  const values = products
    .map(p => parseFloat(p.area_original))
    .filter(a => !isNaN(a) && a > 0)
  if (values.length === 0) return [0, 0]
  // Floor both bounds so each tick N represents the bucket [N, N+step).
  // apply uses >= lo and < hi+step — products always have area >= lo so no under-inclusion.
  return [
    Math.floor(Math.min(...values) / step) * step,
    Math.floor(Math.max(...values) / step) * step,
  ]
}

/**
 * Returns null if no displayable product has a valid price (Case E).
 * Otherwise returns [roundedMin, roundedMax] — rounded to nearest 10 000.
 * Products with no valid price are excluded from the range calculation but
 * remain visible when the filter is at default (universal skip rule).
 *
 * @param {LocalProduct[]} products
 * @returns {[number, number] | null}
 */
function getPriceRange(products) {
  const step = RANGE_STEPS[FILTER_IDS.PRICE_RANGE]
  const prices = products.map(p => resolvePrice(p)).filter(price => price !== null)
  if (prices.length === 0) return null
  const min = Math.min(...prices)
  const max = Math.max(...prices)
  // Floor both bounds so each tick N represents the bucket [N, N+step).
  // apply uses >= lo and < hi+step — products always have price >= lo so no under-inclusion.
  return [
    Math.floor(min / step) * step,
    Math.floor(max / step) * step,
  ]
}

function getTiersList(products) {
  const tiers = new Set()
  for (const p of products) {
    const arr = Array.isArray(p.tiers) ? p.tiers : []
    // Each tier entry is an object { ref_tier, windows, cameras, ... }
    arr.forEach(t => { if (t?.ref_tier != null && !isNaN(t.ref_tier)) tiers.add(t.ref_tier) })
  }
  return [...tiers].sort((a, b) => a - b)
}

function getStatusList(products) {
  return [...new Set(products.map(p => p.status).filter(Boolean))]
}

function getBuildingIds(products) {
  return [...new Set(products.map(p => p.building_name ?? p.building_id).filter(Boolean))]
}

/**
 * Collects all unique cardinal directions from product exposition data.
 * Returns a sorted array — e.g. ['E', 'N', 'NE', 'NW', 'S', 'SE', 'SW', 'W'].
 * Empty when no product has exposition data.
 */
function getExpositionOptions(products) {
  const values = new Set()
  for (const p of products) {
    const expo = Array.isArray(p.exposition) ? p.exposition : []
    for (const v of expo) values.add(v)
  }
  return [...values].sort()
}

/**
 * Collects unique outdoor compartment type IDs that actually appear in product data.
 */
function getExternalAreaOptions(products, compartments) {
  let compartmentList = []
  if (Array.isArray(compartments)) {
    compartmentList = compartments
  } else if (compartments && typeof compartments === 'object') {
    const src = compartments.compartments_types ?? compartments
    compartmentList = Object.values(src)
  }

  const outdoorTypes = new Set(
    compartmentList
      .filter(c => c?.group === 'OutdoorSpace')
      .map(c => c.id),
  )

  if (outdoorTypes.size === 0) return []

  const found = new Set()
  for (const p of products) {
    for (const c of p.compartments ?? []) {
      if (outdoorTypes.has(c.name_cad)) found.add(c.name_cad)
    }
  }
  return [...found].sort()
}

/**
 * Returns the insolation bucket strings present across all products,
 * sorted in canonical order: 'null', '0', '1', … '10'.
 *
 * Returns [] when no product has any sun data (all sums === 0 and no null ranges)
 * — same skip condition as before, so the insolation filter stays hidden when
 * the project contains no insolation measurements.
 */
function getInsolationBuckets(products) {
  const sums = products.map(p => sumSunLightRanges(p.sun_light_ranges))
  const maxSum = sums.length > 0 ? Math.max(...sums) : 0

  if (maxSum === 0) return []

  const ORDER = ['null', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
  const found = new Set(products.map(p => getInsolationBucket(p.sun_light_ranges)))
  return ORDER.filter(b => found.has(b))
}

/**
 * Derives the set of exposition-sides tag values present across all products,
 * returned in canonical order: ['corner', '1', '2', '3', '4+'] (only present tags).
 *
 * A tag is present when at least one product classifies into it.
 * Returns [] when no product has exposition data — filter stays hidden.
 */
function getExpositionSidesBuckets(products) {
  const present = new Set()
  for (const p of products) {
    const tags = classifyExpositionSides(p.exposition ?? [])
    if (tags) tags.forEach(t => present.add(t))
  }
  return EXPOSITION_SIDES_ORDER.filter(t => present.has(t))
}

/**
 * Returns category strings actually present in product data, ordered by
 * canonical ALL_PRODUCT_CATEGORIES enum order.
 * Returns [] when no product has a product_category value.
 *
 * @param {LocalProduct[]} products
 * @returns {string[]}
 */
function getCategoryOptions(products) {
  const present = new Set(products.map(p => p.product_category).filter(Boolean))
  return ALL_PRODUCT_CATEGORIES.filter(c => present.has(c))
}

/**
 * Returns all 10 decile bucket strings ('p0'–'p9') when any product has
 * a positive area_original value. Returns [] when no area data is available.
 *
 * The full set is always returned because deciles are relative — every bucket
 * is always occupied by definition (computed from the same product set).
 *
 * @param {LocalProduct[]} products
 * @returns {string[]}
 */
function getCompareAreaOptions(products) {
  const hasArea = products.some(p => p.area_original > 0)
  if (!hasArea) return []
  return ['p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8', 'p9']
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Builds the default filter configuration from product and supplementary data.
 *
 * Returns the full possible range / all-options set for each filter key.
 * This is the "nothing is filtered" baseline — i.e. every product passes
 * when filtersConfig equals the value returned here.
 *
 * Active initial state (e.g. sold filtered out by panelSettings) is applied
 * separately by useFilters as an overlay on top of this baseline.
 *
 * Caller is responsible for passing only displayable products — hidden products
 * must be filtered out before calling this function (in useProducts/useFilters).
 *
 * Strict-match filters use `{ selected: [], strict: false, options: [...] }` shape:
 *  - `selected` = default active state (empty = no filtering → skip rule fires).
 *  - `options`  = all available values for the MultiSelect UI (derived from product data).
 *  - `exposition` — options = unique cardinal directions from products.
 *  - `external_areas` — options = outdoor compartment types present in products.
 *  - `additional_areas` — still options-less (CMS-driven, TODO).
 *
 * @param {Object}         ctx
 * @param {LocalProduct[]} ctx.products       — displayable products only (caller pre-filters by visibility)
 * @param {Object[]}       ctx.buildings      — buildings array from buildings.json (reserved for future use)
 * @param {Object|Array}  [ctx.compartments]  — compartments data from compartments.json;
 *                                              either a map `{ [id]: { id, group, ... } }` or an array
 * @returns {FiltersConfig}
 */
export function buildInitialFilters({ products, buildings, compartments = {} }) {
  return {
    [FILTER_IDS.ROOMS]:            getRoomsRange(products),
    [FILTER_IDS.AREA]:             getAreaRange(products),
    [FILTER_IDS.PRICE_RANGE]:      getPriceRange(products),   // null = no valid prices anywhere
    [FILTER_IDS.PRICE_INPUT]:      getPriceRange(products),   // mirrors price_range — same data, different UI
    [FILTER_IDS.TIERS]:            getTiersList(products),
    [FILTER_IDS.INSOLATION]:       getInsolationBuckets(products),
    [FILTER_IDS.EXPOSITION_SIDES]: getExpositionSidesBuckets(products),
    [FILTER_IDS.BUILDINGS]:        getBuildingIds(products),
    [FILTER_IDS.STATUS]:           getStatusList(products),
    [FILTER_IDS.UNIT_LEVELS]:      [UNIT_LEVELS.SINGLE, UNIT_LEVELS.MULTI],
    // Strict-match filters: selected=[] is the "no filtering" default (skip rule fires).
    // options[] supplies the MultiSelect dropdown — derived from product data.
    [FILTER_IDS.EXPOSITION]:       { selected: [], strict: false, options: getExpositionOptions(products) },
    [FILTER_IDS.EXTERNAL_AREAS]:   { selected: [], strict: false, options: getExternalAreaOptions(products, compartments) },
    [FILTER_IDS.ADDITIONAL_AREAS]: { selected: [], strict: false },
    // Text search: empty string = no filtering (skip rule fires in filterProducts)
    [FILTER_IDS.UNIT_NAME_SEARCH]: '',
    // Classification: derived from product data, canonical enum order
    [FILTER_IDS.CATEGORY]:         getCategoryOptions(products),
    [FILTER_IDS.COMPARE_AREA]:     getCompareAreaOptions(products),
  }
}
